// Types for video segment annotation
export interface VideoSegment {
    id: string;
    beginTime: number; // in seconds
    endTime: number;   // in seconds
    text: string;
}

export interface VideoSegmentAnnotation {
    videoDuration: number; // in seconds
    segments: VideoSegment[];
}


// new Set<number>(


export enum ActionType {
    Waiting = "Waiting",
    Organizing = "Organizing",
    GettingIntoPosition = "Getting Into Position",
    SwitchingSide = "Switching Side",
    Ready = "Ready",
    PerformingExercise = "Performing Exercise",
    Finished = "Finished",

    Interrupted = "Interrupted",
    Other = "Other",
    Unannotated = "Unannotated",

    SettingUpCamera = "Setting Up The Camera",
    NoUser = "No User in Frame",
    Resting = "Resting",
    BadCameraSetup = "Exercising - Bad Camera Setup",
}

export const ActionTypeList: ActionType[] = [
    ActionType.PerformingExercise, 
    ActionType.GettingIntoPosition,
    ActionType.Organizing, ActionType.SettingUpCamera, ActionType.Interrupted,
    ActionType.Finished, ActionType.NoUser,

    ActionType.SwitchingSide, 
    ActionType.Ready, 
    //ActionType.Waiting,
    ActionType.Other, 
    ActionType.Unannotated,
    ActionType.Resting,
    ActionType.BadCameraSetup
]

// Action segment color mapping
export const ActionTypeColorMap: Record<ActionType, string> = {
    [ActionType.Organizing]: "#FFB74D",   // Orange
    [ActionType.Waiting]: "#FFFF00",      // Yellow
    [ActionType.GettingIntoPosition]: "#c6f2b4ff",      // Lime
    [ActionType.Ready]: "#81C784",        // Light Green
    [ActionType.PerformingExercise]: "#4CAF50",     // Green
    [ActionType.SwitchingSide]: "#3309eeff", // Blue
    [ActionType.Interrupted]: "#E57373",  // Light Red
    [ActionType.Finished]: "#64B5F6",     // Blue
    [ActionType.Other]: "#BA68C8",        // Purple
    [ActionType.Unannotated]: "#E0E0E0",   // Grey
    [ActionType.SettingUpCamera]: "#FFFF88", // Light Yellow  
    [ActionType.NoUser]: "#000000",        // black
    [ActionType.Resting]: "#95bd97ff",        // Light Green
    [ActionType.BadCameraSetup]: "rgb(77, 120, 86)",        
};

export interface ActionAnnotation {
    action   : ActionType
    comment? : string
}

export interface Segment {
    index    : number;
    beginTime: number; // in seconds
    endTime: number;   // in seconds
    annotation: ActionAnnotation;
}


export class VideoAnnotation {
    duration: number=0; 
    splitPoints: Set<number>=new Set<number>();
    segments: Segment[]=[];

    constructor(duration: number = 0,  splitPoints?: Set<number>,  segments?: Segment[]) {

        this.duration = duration;

        if (!splitPoints || !segments) {
            this.segments = [ {
                index: 0,
                beginTime: 0,
                endTime: duration,
                annotation: { action: ActionType.Unannotated, comment: ""}
            }];
            this.splitPoints = new Set<number>();
            return;
        }

        this.splitPoints = splitPoints ;
        this.segments    = segments;
    }


    splitAt(time: number) {
        // Find the segment that contains this time
        const segmentIndex = this.findIndexByTime(time);
        
        // If no segment contains this time, or time is at segment boundary, return unchanged
        if (segmentIndex === -1 || this.splitPoints.has(time)) {
            return this.clone();
        }
        
        const segmentToSplit = this.segments[segmentIndex];
        
        // Don't split if time is at the beginning or end of the segment
        if (time <= segmentToSplit.beginTime || time >= segmentToSplit.endTime) {
            return this.clone();
        }
        
        // Create first part of split segment
        const firstPart: Segment = {
            index: segmentToSplit.index,
            beginTime: segmentToSplit.beginTime,
            endTime: time,
            annotation: { ...segmentToSplit.annotation }
        };
        
        // Create second part of split segment
        const secondPart: Segment = {
            index: segmentToSplit.index + 1,
            beginTime: time,
            endTime: segmentToSplit.endTime,
            annotation: { ...segmentToSplit.annotation }
        };
        
        // Update current object in place
        this.segments.splice(segmentIndex, 1, firstPart, secondPart);
        
        // Reindex all segments
        this.reindex();
        
        // Add split point to current object
        this.splitPoints.add(time);
        
        // Return a new copy
        return this.clone();
    }

    findIndexByTime(time: number): number {
        // Find the segment that contains this time
        return this.segments.findIndex(segment => 
            time >= segment.beginTime && time < segment.endTime
        );
    }

    reindex() {
        // Fix all segment indexes to be sequential starting from 0
        for (let i = 0; i < this.segments.length; i++) {
            this.segments[i].index = i;
        }
    }


    updateSegmentAnnotation(index: number, annotation: ActionAnnotation) {
        // Check if the index is valid
        if (index < 0 || index >= this.segments.length) {
            return this.clone();
        }
        
        // Update the annotation of the segment at the given index
        this.segments[index].annotation = { ...annotation };
        
        // Return a deep copy
        return this.clone();
    }

    deleteSegment(index: number) {
        // cannot delete first segment
        if (index <= 0 || index >= this.segments.length) {
            return this.clone();
        }
        const segmentToDelete = this.segments[index];
        // Remove the split point at the start time of the segment to delete
        this.splitPoints.delete(segmentToDelete.beginTime);

        // Merge with previous segment
        const previousSegment = this.segments[index - 1];
        previousSegment.endTime = segmentToDelete.endTime;

        // Remove the segment
        this.segments.splice(index, 1);


        // Reindex all segments
        this.reindex();

        // Return a new copy
        return this.clone();

    }

    clone(): VideoAnnotation {
        return new VideoAnnotation(this.duration, new Set(this.splitPoints), [...this.segments]);
    }

    json() : any {
        return {
            duration: this.duration,
            segments: this.segments.map(segment => ({
                beginTime: segment.beginTime,
                endTime: segment.endTime,
                action: segment.annotation.action,
                comment: segment.annotation.comment
            }))
        }
    }
}
